import * as SearchSuggestionClick from '@ori-events/search-suggestion-click';
import { styled, Typography } from '@ori-ui/mui';
import { ContentType, Placement } from '@ori/analytics-data';
import { useToken } from '@ori/auth';
import { useApiClient, createApplicationsNavigationSignal } from '@ori/global-api-client-react';
import useTestId from '@ori/testid-generator';
import type { FC } from 'react';
import { useCallback } from 'react';

import { useAppConfig } from '../../context';
import { useSearchInput } from '../../context/navigation';
import { useTranslations } from '../../hooks/useTranslations';
import { type Product } from '../../models';
import { analytics } from '../../utils';
import { ProductTile } from '../common/ProductTile';

const PopularProductsRoot = styled('div')(({ theme }) => ({
  boxSizing: 'border-box',
  height: '100%',
  position: 'relative',
  width: '100%',

  [theme.breakpoints.up('md')]: {
    display: 'flex',
    flexDirection: 'column',
    gridColumn: 2,
    gridRow: '1 / 3',
    margin: 0,
    padding: '0 calc(10% - 60px) 0 10%',
    textAlign: 'center',

    '&::before': {
      backgroundColor: theme.palette.grey[100],
      content: '""',
      height: '100%',
      left: 0,
      marginLeft: theme.spacing(5),
      position: 'absolute',
      width: 4,
    },
  },
}));

const PopularProductsHeader = styled(Typography)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightBold,
  marginBottom: theme.spacing(3),

  [theme.breakpoints.up('md')]: {
    marginBottom: theme.spacing(-6),
  },
}));

const PopularProductsWrapper = styled('div')(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  margin: 0,
  padding: 0,
}));

export const PopularProducts: FC = () => {
  const globalApiClient = useApiClient();
  const { formatMessage } = useTranslations();
  const { currentLanguage } = useAppConfig();
  const { customerId } = useToken();
  const { getTestId } = useTestId();
  const { results, timestamp } = useSearchInput();

  const onClick = useCallback(
    (position: number) => {
      if (results?.Products.length == null) {
        return;
      }

      const searchSuggestionClickData: SearchSuggestionClick.Data = {
        itemData: analytics.formatProduct(results.Products[position] as Product),
        itemType: ContentType.Product,
        searchQuery: results.Query,
        placement: Placement.TopNavigationSearchSuggestions,
        position,
      };

      createApplicationsNavigationSignal(
        globalApiClient,
        analytics.getSignalsData({ customerId, position, results, timestamp }),
        // eslint-disable-next-line @typescript-eslint/naming-convention -- generated typings require headers
        { 'Accept-Language': currentLanguage },
        // eslint-disable-next-line no-console -- log error to console
      ).catch(console.error);

      window.dispatchEvent(
        new CustomEvent<SearchSuggestionClick.Data>(SearchSuggestionClick.Name, {
          detail: searchSuggestionClickData,
        }),
      );
    },
    [results, globalApiClient, customerId, timestamp, currentLanguage],
  );

  if (!results?.Products) {
    return null;
  }

  return (
    <PopularProductsRoot data-testd={getTestId('popular-products-root')}>
      <PopularProductsHeader>{formatMessage('popularProductsInThisSearchQuery')}</PopularProductsHeader>
      <PopularProductsWrapper>
        {results.Products.map(({ ProductCode, StandardPrice, ActualPrice, ProductImageUrl, ConceptName }, index) => (
          <ProductTile
            key={ProductCode}
            displayProductName
            largeVersion
            basicCataloguePrice={StandardPrice}
            currentPrice={ActualPrice}
            imageUrl={ProductImageUrl}
            position={index}
            productCode={ProductCode}
            productName={ConceptName}
            onClick={onClick}
          />
        ))}
      </PopularProductsWrapper>
    </PopularProductsRoot>
  );
};
